import React from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, MessageSquare, Zap, BrainCircuit } from 'lucide-react';
import { useActivities } from '../context/ActivitiesContext';
import { cn } from '../utils';

const ActivityItem = ({ type, title, subtitle, participants, avatarCount }) => (
  <div className="p-4 hover:bg-white cursor-pointer rounded-3xl transition-all duration-300 border border-transparent hover:border-gray-100 group shadow-sm hover:shadow-2xl hover:shadow-brand-indigo/5 hover:scale-[1.02]">
    <div className="flex items-start gap-4">
      <div className="mt-1 p-2 bg-gray-50 rounded-xl group-hover:bg-brand-indigo/5 transition-colors">
        {type === 'live' && <Zap size={14} className="text-brand-accent fill-brand-accent animate-pulse" />}
        {type === 'poll' && <TrendingUp size={14} className="text-brand-indigo" />}
        {type === 'qa' && <MessageSquare size={14} className="text-purple-500" />}
        {type === 'brainstorm' && <BrainCircuit size={14} className="text-green-600" />}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-bold text-gray-900 leading-snug group-hover:text-brand-indigo transition-colors truncate">{title}</h4>
        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1.5 opacity-80">{subtitle}</p>
        <div className="flex items-center gap-2 mt-3">
          <div className="flex -space-x-1.5">
            {Array.from({ length: Math.min(avatarCount || 3, 3) }, (_, index) => (
              <div key={index} className="w-6 h-6 rounded-full border-2 border-white bg-gray-100 overflow-hidden shadow-sm">
                <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${title}-${index}`} alt="avatar" />
              </div>
            ))}
          </div>
          <span className={cn(
            "text-[9px] font-bold px-2 py-1 rounded-full shadow-sm uppercase tracking-widest border",
            type === 'brainstorm' ? "text-green-600 bg-green-50/50 border-green-100" : "text-brand-indigo bg-brand-indigo/5 border-brand-indigo/10"
          )}>+{participants}</span>
        </div>
      </div>
    </div>
  </div>
);

const RightSidebar = () => {
  const navigate = useNavigate();
  const { realtime, trendingHubs } = useActivities();

  return (
    <aside className="w-80 h-[calc(100vh-64px)] sticky top-16 mt-16 bg-white border-l border-gray-100 hidden lg:flex flex-col py-6 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-100 scrollbar-track-transparent">
      <div className="px-5 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(239,68,68,0.5)]"></span>
            Realtime
          </h3>
          <button 
            onClick={() => navigate('/live')}
            className="text-[10px] font-bold text-brand-indigo hover:underline uppercase tracking-widest"
          >
            See all
          </button>
        </div>
        
        <div className="space-y-1">
          {realtime.length > 0 ? (
            realtime.map(room => (
              <ActivityItem 
                key={room.id}
                type={room.type.toLowerCase() === 'poll' ? 'poll' : room.type.toLowerCase() === 'brainstorm' ? 'brainstorm' : room.type.toLowerCase() === 'q&a' ? 'qa' : 'live'} 
                title={room.title} 
                subtitle={`Hosted by ${room.host}`} 
                participants={room.participants >= 1000 ? `${(room.participants/1000).toFixed(1)}k` : room.participants}
                avatarCount={Math.min(3, room.participants || 0)}
              />
            ))
          ) : (
            <p className="text-xs text-gray-400 text-center py-4 italic">No live activities</p>
          )}
        </div>
      </div>

      <div className="px-5">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Trending Hubs</h3>
          <button 
            onClick={() => navigate('/communities')}
            className="text-[10px] font-bold text-brand-indigo hover:underline uppercase tracking-widest"
          >
            See all
          </button>
        </div>
        <div className="space-y-5 px-1">
          {trendingHubs.length > 0 ? (
            trendingHubs.map((community, index) => {
              const colors = ['bg-yellow-400', 'bg-brand-indigo', 'bg-green-600', 'bg-purple-500', 'bg-red-500'];
              return (
                <div key={community.id || community.name} className="flex items-center gap-4 group cursor-pointer p-1 hover:bg-gray-50/50 rounded-2xl transition-all">
                  <div className={`w-12 h-12 rounded-[1.25rem] ${colors[index % colors.length]} flex items-center justify-center text-white font-bold transition-all group-hover:scale-105 shadow-lg group-hover:shadow-brand-indigo/20 shrink-0 border-4 border-white`}>
                    {community.name[0]}
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-sm font-bold text-gray-900 group-hover:text-brand-indigo transition-colors truncate">{community.name}</h4>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1.5 flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                      {(community.members_count || community.members || 0) >= 1000 
                        ? `${((community.members_count || community.members || 0)/1000).toFixed(1)}k` 
                        : (community.members_count || community.members || 0)} members
                    </p>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-xs text-gray-400 text-center py-4 italic">No hubs found</p>
          )}
        </div>
      </div>
    </aside>
  );
};

export default RightSidebar;
